import React, { useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { THEME_LABELS, fmtWhen } from "./labels";

const btnDark = "inline-flex items-center gap-2 px-4 py-2 bg-clay-900 text-white text-[12px] tracking-wider uppercase hover:bg-terracotta disabled:opacity-50";

export function AiPanel({ lead, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const ai = lead.ai;
  const labels = lead.loss_reason_labels || {};

  const run = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/kimkim/leads/${lead.lead_id}/analyze`, null, { params: { force } });
      onUpdated(data);
      toast.success("Análisis listo");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo analizar"); }
    finally { setBusy(false); }
  };

  return (
    <section className="border border-clay-300 bg-white p-5" data-testid="kimkim-ai-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div><div className="smallcaps">Lectura con IA</div><h2 className="font-serif text-2xl mt-1">Qué pasó y qué aprender</h2></div>
        {!ai ? (
          <button onClick={() => run(false)} disabled={busy || !(lead.messages || []).length} className={btnDark} data-testid="kimkim-ai-analyze-btn">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Analizar con IA
          </button>
        ) : (
          <button onClick={() => run(true)} disabled={busy} className="text-[11px] uppercase tracking-wider text-clay-600 hover:text-clay-900 inline-flex items-center gap-1" data-testid="kimkim-ai-redo-btn" title="Vuelve a leer la conversación (nuevo coste)">
            {busy ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>} Repetir
          </button>
        )}
      </div>
      {lead.ai_tags && (
        <div className="mb-4 border border-clay-200 bg-clay-50 px-3 py-2 text-[12px] flex flex-wrap gap-x-4 gap-y-1" data-testid="kimkim-ai-tags">
          <span className="smallcaps">Etiquetas del estudio</span>
          {lead.ai_tags.loss_reason && <span>Motivo: <strong>{labels[lead.ai_tags.loss_reason] || lead.ai_tags.loss_reason}</strong></span>}
          {lead.ai_tags.first_reply_personalized != null && <span>1ª respuesta: <strong>{lead.ai_tags.first_reply_personalized ? "personalizada" : "plantilla"}</strong></span>}
          {(lead.ai_tags.themes || []).length > 0 && <span>Temas: {lead.ai_tags.themes.map((t) => THEME_LABELS[t] || t).join(", ")}</span>}
          {lead.ai_tags.objection && <span className="basis-full italic text-clay-700">«{lead.ai_tags.objection}»</span>}
        </div>
      )}
      {!ai ? (
        <p className="text-sm text-clay-600">La IA leerá la conversación completa y devolverá la demanda, la gestión, el motivo probable de pérdida, qué funcionó y qué mejorar. Se guarda y no se vuelve a cobrar al reabrir. Coste orientativo: céntimos.</p>
      ) : (
        <div className="space-y-4 text-sm" data-testid="kimkim-ai-result">
          {ai.loss_reason && (
            <div className="inline-flex items-center gap-2 border border-terracotta/40 bg-terracotta/5 px-3 py-1.5" data-testid="kimkim-ai-loss-reason">
              <span className="smallcaps">Motivo de pérdida</span><span className="font-semibold">{labels[ai.loss_reason] || ai.loss_reason}</span>
              {ai.loss_confidence && <span className="text-[11px] text-clay-600">confianza {ai.loss_confidence}</span>}
            </div>
          )}
          <div><div className="smallcaps mb-1">Demanda</div><p>{ai.summary}</p></div>
          <div><div className="smallcaps mb-1">Gestión</div><p>{ai.management}</p></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><div className="smallcaps mb-1 text-emerald-800">Qué funcionó</div><ul className="list-disc pl-5 space-y-1">{(ai.worked || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            <div><div className="smallcaps mb-1 text-terracotta">Qué mejorar</div><ul className="list-disc pl-5 space-y-1">{(ai.improve || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          </div>
          <div className="flex flex-wrap gap-1.5">{(ai.tags || []).map((t) => <span key={t} className="border border-clay-300 px-2 py-0.5 text-[11px] bg-clay-50">{t}</span>)}</div>
          <div className="text-[11px] text-clay-500">{ai.model} · {fmtWhen(ai.analyzed_at)}</div>
        </div>
      )}
    </section>
  );
}
